// ─────────────────────────────────────────────
//  Messaging domain — pure, Firestore-free, React-free helpers for the
//  real-time chat data layer (mirrors src/domain/alerts.js / channels.js).
//
//  This module holds the three "test seam" computations so they unit-test
//  without the emulator or React:
//    • diffChannelDoc  — should we re-materialize a channel doc? (Pitfall 5
//                        diff-before-write — only write on a REAL change)
//    • unreadCount     — global badge count (client-side, no server counter)
//    • mergeChannelMessages — flatten the per-channel snapshot map into one
//                        sorted, de-duplicated list (mirrors useConversations)
// ─────────────────────────────────────────────

// Order-insensitive set equality for two id arrays.
function sameMemberSet(a = [], b = []) {
  const aa = Array.isArray(a) ? a : [];
  const bb = Array.isArray(b) ? b : [];
  if (aa.length !== bb.length) return false;
  const setB = new Set(bb);
  for (const id of aa) {
    if (!setB.has(id)) return false;
  }
  return true;
}

// ── diffChannelDoc (Pitfall 5: diff-before-write) ──
// Returns true ONLY when the freshly-computed channel doc differs from the
// existing one — so materialization writes only on a real membership/label
// change and a burst of board edits does not storm `channels` writes.
// memberIds compare is order-insensitive; a missing existing doc → true (first
// write). Compares the materialized fields: { label, memberIds, postableBy }.
export function diffChannelDoc(existing, computed = {}) {
  if (!existing) return true;
  if (existing.label !== computed.label) return true;
  if (existing.postableBy !== computed.postableBy) return true;
  if (!sameMemberSet(existing.memberIds, computed.memberIds)) return true;
  return false;
}

// ── unreadCount (client-side badge, no server counter) ──
// Channel messages: count those NOT sent by me AND not yet in my readBy.
// DMs: count those addressed to me AND not isRead.
// Pure — returns the TRUE number (the caller renders ">99" as "99+").
export function unreadCount(channelMessages, dmMessages, uid) {
  const channels = Array.isArray(channelMessages) ? channelMessages : [];
  const dms = Array.isArray(dmMessages) ? dmMessages : [];
  const channelUnread = channels.filter(
    (m) => m && m.senderId !== uid && !((m.readBy || []).includes(uid))
  ).length;
  const dmUnread = dms.filter(
    (m) => m && m.recipientId === uid && !m.isRead
  ).length;
  return channelUnread + dmUnread;
}

// ── mergeChannelMessages (mirror useConversations merge) ──
// Flattens a { channelId: messages[] } map into ONE array, de-duplicated by id
// and sorted ascending by sentAt (messages with no sentAt sort first as 0).
export function mergeChannelMessages(perChannelMap) {
  if (!perChannelMap || typeof perChannelMap !== "object") return [];
  const seen = new Set();
  const flat = [];
  for (const list of Object.values(perChannelMap)) {
    if (!Array.isArray(list)) continue;
    for (const msg of list) {
      if (!msg || seen.has(msg.id)) continue;
      seen.add(msg.id);
      flat.push(msg);
    }
  }
  const ms = (m) => (m.sentAt?.toMillis ? m.sentAt.toMillis() : 0);
  flat.sort((a, b) => ms(a) - ms(b));
  return flat;
}

// ── deriveDmContacts (D-08: DM contacts = today's roster only) ──
// Builds the 1:1 DM contact list from today's roster, resolving each roster
// entry to its auth uid via the staff link (the same staff-id→uid resolution
// channels.js uses — a null-uid pool-only person CANNOT log in to chat and is
// dropped). The current user is excluded. Each contact carries a role/subRole
// meta line (D-08 contract: DMs show role meta, not member counts).
//
//   roster: { aicId, aicName, supervisors:[{id,name,...}], crnas:[{id,name,...}] }
//   staff:  normalized pool entries [{ id, uid, name, role, subRole }]
//   uid:    the current user's auth uid (excluded from the result)
//
// Returns: [{ id:<uid>, name, meta }] sorted by name. Pure — no Firestore/React.
export function deriveDmContacts(roster, staff, uid) {
  const pool = Array.isArray(staff) ? staff : [];
  // Resolve a roster entry's staff doc id (or already-a-uid) → auth uid + meta.
  const entryFor = (id) => pool.find((s) => s && (s.id === id || s.uid === id)) || null;

  // role/subRole → human meta line ("CRNA", "Anesthesiologist", "AA", …).
  const metaFor = (entry, isAic) => {
    if (!entry) return isAic ? "AIC" : "Direct message";
    const sub = (entry.subRole || "").toUpperCase();
    const base =
      entry.role === "anesthesiologist"
        ? "Anesthesiologist"
        : sub || "Provider";
    return isAic ? `${base} · AIC` : base;
  };

  const seen = new Set();
  const contacts = [];
  const add = (rosterId, fallbackName, isAic) => {
    const entry = entryFor(rosterId);
    // Resolve to the auth uid: a pool-only (null-uid) person can't chat — drop.
    const contactUid = entry?.uid || null;
    if (!contactUid || contactUid === uid || seen.has(contactUid)) return;
    seen.add(contactUid);
    contacts.push({
      id: contactUid,
      name: (entry?.name || fallbackName || "").trim() || "Direct message",
      meta: metaFor(entry, isAic),
    });
  };

  if (roster?.aicId) add(roster.aicId, roster.aicName, true);
  for (const s of roster?.supervisors || []) add(s?.id, s?.name, false);
  for (const c of roster?.crnas || []) add(c?.id, c?.name, false);

  contacts.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  return contacts;
}
